import React, { useCallback } from 'react';

import { TextOnlyButton } from '@rbilabs/components-library/build/components/button';
import { Icon } from '@rbilabs/components-library/build/components/icon';
import styled, { css } from 'styled-components';

import { IBaseProps, OnClick } from '@rbi-ctg/frontend';
import { IOffer } from '@rbi-ctg/menu';
import Animation from 'components/lottie-animations/favorite-loading/favorite-loading';
import { OfferBadges } from 'components/offer-badges';
import {
  IMG_FIT_CONTAIN,
  INCLUDE_BG_IMG,
  SELECTED_OFFER_BORDER,
} from 'components/offers/ui-refresh/constants';
import Picture from 'components/picture';
import SanityBlockRenderer, {
  IBlockContentOverrides,
  ISerializers,
  PassthroughComponent,
} from 'components/sanity-block-renderer';
import { UIPattern } from 'state/offers/types';
import noop from 'utils/noop';
import { IDays, IHours } from 'utils/offers/rule-sets/time-left-to-redeem';
import { stylesForMaxLinesOfText } from 'utils/style/max-lines-text';

import theme from './theme';

const Wrapper = styled.li`
  width: 100%;
  border-bottom: 1px solid ${Styles.color.grey.five};
`;

export const OFFER_SIDES_PADDING = '14px';
export const OFFER_TOP_BOTTOM_PADDING = '8PX';
export const OFFER_CONTAINER_PADDING = `${OFFER_TOP_BOTTOM_PADDING} ${OFFER_SIDES_PADDING}`;

export const StyledClickableContainer = styled.button<{ disabled: boolean }>`
  /* reset button styles */
  background: transparent;
  border: none;
  text-align: left;

  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  padding: ${OFFER_CONTAINER_PADDING};
  background-color: ${p => (p.disabled ? Styles.color.grey.five : Styles.color.white)};

  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
`;

/**
 * This component avoids scenarios where a typical `border` on the Wrapper
 * would make the selected Offer slightly larger than its non-selected siblings
 */
const SelectedState = styled.div<{ selected: boolean; disabled: boolean }>`
  position: absolute;
  right: 0;
  top: 0;
  width: 10px;
  height: 100%;
  border-right: ${p => `10px solid ${p.selected ? SELECTED_OFFER_BORDER : Styles.color.grey.four}`};

  transition: opacity 0.25s;
  opacity: ${p => (p.selected ? 1 : 0)};
  &:hover {
    ${p =>
      !p.disabled &&
      !p.selected &&
      css`
        opacity: 1;
      `}
  }
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 100%;
`;

export const OFFER_HEIGHT_MOBILE = '110px';
export const OFFER_HEIGHT_DESKTOP = '138px';

export const PictureContainer = styled.div`
  position: relative;
  width: 130px;
  height: ${OFFER_HEIGHT_MOBILE};
  border-radius: 8px;
  background-color: ${Styles.color.background};

  ${Styles.desktop`
    width: 164px;
    height: ${OFFER_HEIGHT_DESKTOP};
  `}
`;

const BackgroundPicture = styled(Picture)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const StyledPicture = styled(Picture)`
  width: 100%;
  height: 100%;
`;

const TextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 16px;
`;

const Name = styled.h3`
  margin: 0;
  margin-bottom: 4px;
  text-transform: ${theme.headingTextTransform};
  font-family: ${Styles.fontFamily.base};
  color: ${Styles.color.black};

  ${stylesForMaxLinesOfText({
    maxLines: 2,
    lineHeight: 1.5,
    fontSizeInRem: 1,
  })}

  ${Styles.desktop`
    margin-bottom: 8px;
    ${stylesForMaxLinesOfText({
      maxLines: 2,
      lineHeight: 1.5,
      fontSizeInRem: 1,
    })}
  `}
`;

const nameBlockContentOverrides: IBlockContentOverrides = {
  normal: Name,
};

const nameBlockSerializers: ISerializers = {
  marks: {
    em: PassthroughComponent,
  },
  container: PassthroughComponent,
};

const Description = styled.p`
  margin: 0;
  font-family: ${Styles.fontFamily.body};
  color: ${theme.descriptionColor};

  ${stylesForMaxLinesOfText({ maxLines: 2, lineHeight: 1.4, fontSizeInRem: 0.875 })}
`;

const descriptionBlockContentOverrides: IBlockContentOverrides = {
  normal: Description,
};

const descriptionBlockSerializers: ISerializers = {
  container: PassthroughComponent,
};

const FavoriteButton = styled(TextOnlyButton)`
  border-bottom: none;

  &:focus {
    border-bottom: none;
  }

  && svg {
    fill: ${Styles.color.black};
  }
`;
export const StyledOfferBadges = styled(OfferBadges)`
  justify-content: flex-start;
  &:not(:empty) {
    margin-top: 8px;
  }
`;

export const LoadingWrapper = styled.div`
  height: 2.125rem;
  width: 2.125rem;
  margin-top: 3px;
  margin-right: -2px;
`;

interface ICellLayoutProps extends IBaseProps {
  offer: IOffer;
  onClick: OnClick;
  isSelected: boolean;
  redeemable: boolean;
  enableOfferBadges: boolean;
  endingIn: IDays | IHours | null;
  platforms: string[];
  displayLoginRequiredBadge: boolean;
  favorited?: boolean;
  favoriteEnabled?: boolean;
  favoriteLoading?: boolean;
  handleToggleFavorite?: (offerId: string) => void;
}

export const CellLayout: React.FC<ICellLayoutProps> = ({
  className = '',
  offer,
  onClick,
  isSelected,
  redeemable,
  enableOfferBadges,
  endingIn,
  platforms,
  displayLoginRequiredBadge,
  favorited = false,
  favoriteEnabled = false,
  favoriteLoading = false,
  handleToggleFavorite = noop,
}) => {
  const disableCell = !redeemable;

  const handleFavoriteClick = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      handleToggleFavorite(offer._id);
    },
    [handleToggleFavorite, offer._id]
  );

  return (
    <Wrapper className={className} data-testid={`${offer._id}__offer`}>
      <StyledClickableContainer onClick={onClick} disabled={disableCell}>
        <ContentRow>
          <PictureContainer>
            {INCLUDE_BG_IMG && offer.backgroundImage && (
              <BackgroundPicture alt="" image={offer.backgroundImage} objectFitContain={false} />
            )}
            {offer?.localizedImage?.locale?.app && (
              <StyledPicture
                alt={offer.localizedImage?.locale.imageDescription ?? ''}
                image={offer.localizedImage?.locale?.app}
                objectFitContain={IMG_FIT_CONTAIN}
              />
            )}
          </PictureContainer>
          <TextContainer data-testid={`cell-layout-text-container-${offer._id}`}>
            <SanityBlockRenderer
              blockContentOverrides={nameBlockContentOverrides}
              serializers={nameBlockSerializers}
              content={offer?.name?.localeRaw}
            />
            <SanityBlockRenderer
              blockContentOverrides={descriptionBlockContentOverrides}
              serializers={descriptionBlockSerializers}
              content={offer?.description?.localeRaw}
            />
            {enableOfferBadges && (
              <StyledOfferBadges
                platforms={platforms}
                displayLoginRequiredBadge={displayLoginRequiredBadge}
                endingIn={endingIn}
                uiPattern={offer.uiPattern as UIPattern}
                isUnlocked={!!offer.isRedeemable}
              />
            )}
          </TextContainer>
          {favoriteEnabled && (
            <FavoriteButton onClick={handleFavoriteClick} data-testid="favorite-button">
              {favoriteLoading ? (
                <LoadingWrapper>
                  <Animation />
                </LoadingWrapper>
              ) : (
                <Icon
                  aria-hidden
                  color="icon-default"
                  height="3rem"
                  icon={favorited ? 'favoriteFill' : 'favorite'}
                  width="2rem"
                />
              )}
            </FavoriteButton>
          )}
        </ContentRow>
        <SelectedState
          data-testid={`${offer._id}__offer-selected-state`}
          selected={isSelected}
          disabled={disableCell}
        />
      </StyledClickableContainer>
    </Wrapper>
  );
};
